/**
 * The "AI-generated" label (PRD-264 EUAI-08c, EU AI Act Art. 50(4) and 50(5)).
 *
 * Shown by the byline of every post the server flags `ai_generated` (an
 * agent wrote it and no person has marked it reviewed), on the post cards
 * and on the post page. Its colours are literal values in
 * `styles/ai-label.ts`, not theme variables, so a `themeOverrides` colour
 * can't hide it.
 */

import type { BlogPost } from '@automatos/core';
import { h } from '../dom/create-element';

/** The visible label text (PRD-264 §8, blog widget row). */
export const AI_GENERATED_TEXT = 'AI-generated';

/** Tooltip and accessible description; says what the label means. */
export const AI_GENERATED_TITLE = 'This post was generated by AI and has not been reviewed by a person.';

/** Marks the label, and the card or post page that carries it. */
export const AI_GENERATED_ATTR = 'data-ai-generated';

/** Hook the tests and host pages use to find the label itself. */
export const AI_LABEL_ATTR = 'data-ai-label';

/** True when the post must carry the label. Only an explicit `true` counts. */
export function isAIGenerated(post: BlogPost): boolean {
  return post.ai_generated === true;
}

/** The label element: visible text, plus a title that spells out its meaning. */
export function buildAIGeneratedLabel(): HTMLElement {
  return h('span', {
    class: 'aw-ai-label',
    title: AI_GENERATED_TITLE,
    [AI_LABEL_ATTR]: 'true',
  }, [AI_GENERATED_TEXT]);
}

/**
 * The label as a list to spread into a byline: `[label]` when the post is
 * AI-generated, `[]` otherwise.
 */
export function aiGeneratedLabelItems(post: BlogPost): HTMLElement[] {
  return isAIGenerated(post) ? [buildAIGeneratedLabel()] : [];
}

/**
 * Attributes to spread into a card or post page root, so styles and
 * machines can tell the post is AI-generated. Returns a new object.
 */
export function aiGeneratedAttrs(post: BlogPost): Record<string, string> {
  return isAIGenerated(post) ? { [AI_GENERATED_ATTR]: 'true' } : {};
}
